/**
 * Member List Card
 * Configure the list of members who can be selected as on-call
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  DefaultButton,
  PrimaryButton,
  Dialog,
  DialogType,
  DialogFooter,
  Checkbox,
  SearchBox,
  MessageBarType,
  Spinner,
  SpinnerSize,
  useTheme
} from '@fluentui/react';
import { useOnCallManager } from '../contexts/OnCallManagerContext';
import type { OnCallMember } from '../types';

interface MemberListCardProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const MemberListCard: React.FC<MemberListCardProps> = ({ onNotification }) => {
  const { 
    availableMembers, 
    onCallMembers, 
    updateOnCallMembers,
    loading 
  } = useOnCallManager();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();

  // Load current on-call members when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      setSelectedMembers([...onCallMembers]);
    }
  }, [isDialogOpen, onCallMembers]);

  // Filter members by search text
  const filteredMembers = searchText
    ? availableMembers.filter(m =>
        m.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
        m.email.toLowerCase().includes(searchText.toLowerCase())
      )
    : availableMembers;

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setSearchText('');
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedMembers([]);
  };

  const handleToggleMember = (memberId: string, checked: boolean | undefined) => {
    if (checked) {
      setSelectedMembers(prev => (prev.includes(memberId) ? prev : [...prev, memberId]));
    } else {
      setSelectedMembers(prev => prev.filter(id => id !== memberId));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateOnCallMembers(selectedMembers);
      onNotification(MessageBarType.success, 'On-call members list updated successfully.');
      handleCloseDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update on-call members';
      onNotification(MessageBarType.error, `Error: ${message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderMemberItem = (member?: OnCallMember) => {
    if (!member) return null;
    
    return (
      <div key={member.memberId} style={{ padding: '8px 0', borderBottom: `1px solid ${theme.semanticColors.bodyDivider}` }}>
        <Checkbox
          label={member.fullName}
          checked={selectedMembers.includes(member.memberId)}
          onChange={(_, checked) => handleToggleMember(member.memberId, checked)}
          styles={{
            root: { marginBottom: 4 },
            label: { fontWeight: 500 }
          }}
        />
        <Text variant="small" styles={{ root: { color: theme.semanticColors.bodySubtext, marginLeft: 28 } }}>
          {member.email}
        </Text>
      </div>
    );
  };

  const memberNames = onCallMembers
    .map(id => availableMembers.find(m => m.memberId === id)?.fullName)
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <Stack
        tokens={{ childrenGap: 12, padding: 20 }}
        styles={{
          root: {
            height: '100%',
            backgroundColor: theme.semanticColors.bodyBackground,
            color: theme.semanticColors.bodyText,
            borderRadius: 4,
            boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.132), 0 0.3px 0.9px 0 rgba(0,0,0,0.108)',
            selectors: {
              '.ms-Text': { color: theme.semanticColors.bodyText },
              '.ms-Label': { color: theme.semanticColors.bodyText }
            }
          }
        }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: theme.semanticColors.bodyText } }}>
            On-Call Members List
          </Text>

          {loading ? (
            <Spinner size={SpinnerSize.small} />
          ) : (
            <>
              <Text variant="medium" styles={{ root: { color: theme.semanticColors.bodyText } }}>
                {onCallMembers.length} member{onCallMembers.length !== 1 ? 's' : ''} configured
              </Text>

              {onCallMembers.length > 0 && (
                <Text variant="small" styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
                  {memberNames}
                </Text>
              )}

              <DefaultButton
                text="Configure Members"
                onClick={handleOpenDialog}
                disabled={loading}
              />
            </>
          )}
        </Stack>
      </Stack>

      <Dialog
        hidden={!isDialogOpen}
        onDismiss={handleCloseDialog}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Configure On-Call Members',
          subText: 'Select members who can be assigned as on-call.'
        }}
        modalProps={{
          isBlocking: false,
          styles: {
            main: {
              maxWidth: 600,
              backgroundColor: theme.semanticColors.bodyBackground,
              color: theme.semanticColors.bodyText,
              selectors: {
                '.ms-Text': { color: theme.semanticColors.bodyText },
                '.ms-Label': { color: theme.semanticColors.bodyText }
              }
            }
          }
        }}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <SearchBox
            placeholder="Search members..."
            value={searchText}
            onChange={(_, newValue) => setSearchText(newValue || '')}
            styles={{ root: { width: '100%' } }}
          />

          <Text variant="small" styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
            {selectedMembers.length} of {availableMembers.length} members selected
          </Text>

          <div style={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${theme.semanticColors.bodyDivider}`, padding: 8 }}>
            {filteredMembers.length > 0 ? (
              filteredMembers.map(member => renderMemberItem(member))
            ) : (
              <Text variant="medium" styles={{ root: { padding: 20, textAlign: 'center', color: theme.semanticColors.disabledText } }}>
                No members found
              </Text>
            )}
          </div>
        </Stack>

        <DialogFooter>
          <PrimaryButton
            text={isSaving ? 'Saving...' : 'Save'}
            onClick={handleSave}
            disabled={isSaving}
          />
          <DefaultButton text="Cancel" onClick={handleCloseDialog} disabled={isSaving} />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default MemberListCard;
